/**
 * In-memory stand-in for the tenant palette endpoints: one list per tenant,
 * kept across calls until reset, so a palette added in one chart shows in the
 * next. Rejects the way api-service does (`{ error: { code, message } }`).
 */
import { mockConflictError, mockValidationError } from "../mockDomainError";
import type {
  ColorPaletteApi,
  ColorPaletteListResponseApi,
  CreateColorPaletteRequestApi,
} from "./colorPalette.types";

const LATENCY_MS = 250;
const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const palettesByTenant: Record<string, ColorPaletteApi[]> = {};
let createdCount = 0;

const listFor = (tenantId: string): ColorPaletteApi[] => {
  palettesByTenant[tenantId] ??= [];
  return palettesByTenant[tenantId];
};

/** Deep copy, so a cache holding a result can't reach into the store. */
const copy = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

export async function mockFetchColorPalettes(
  tenantId: string,
): Promise<ColorPaletteListResponseApi> {
  await wait(LATENCY_MS);
  return copy(listFor(tenantId));
}

/**
 * 422 for an empty name or no colours (pydantic's shape), 409 for a name the
 * tenant already uses — both proposed, for the backend to confirm.
 */
export async function mockCreateColorPalette(
  tenantId: string,
  body: CreateColorPaletteRequestApi,
): Promise<ColorPaletteApi> {
  await wait(LATENCY_MS);
  const name = body.name.trim();
  if (!name || body.colors.length === 0) {
    throw mockValidationError("Request validation failed", [
      {
        loc: ["body", name ? "colors" : "name"],
        type: name ? "too_short" : "string_too_short",
        msg: name ? "List should have at least 1 item" : "String should have at least 1 character",
      },
    ]);
  }
  const palettes = listFor(tenantId);
  if (palettes.some((palette) => palette.name.toLowerCase() === name.toLowerCase())) {
    throw mockConflictError(`A palette named "${name}" already exists.`);
  }
  createdCount += 1;
  const createdAt = new Date(Date.UTC(2026, 8, 28, 12, 0, createdCount)).toISOString();
  const created: ColorPaletteApi = {
    id: `mock-palette-${createdCount}`,
    tenant_id: tenantId,
    name,
    colors: [...body.colors],
    created_at: createdAt,
    updated_at: createdAt,
  };
  palettes.push(created);
  return copy(created);
}

/**
 * Any tenant's palette by id, for the canvas-items mock's compile: a chart
 * whose `format.color_palette` is `{ kind: "custom" }` is drawn in it.
 */
export function findMockColorPalette(paletteId: string): ColorPaletteApi | undefined {
  return Object.values(palettesByTenant)
    .flat()
    .find((palette) => palette.id === paletteId);
}

/** Test/Storybook reset. */
export function resetMockColorPalettes(): void {
  Object.keys(palettesByTenant).forEach((tenantId) => delete palettesByTenant[tenantId]);
  createdCount = 0;
}
